import React, { createContext, useContext, useState, useRef, useEffect, ReactNode, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';

export interface GCRosterStudent {
    id: string; // Google Classroom User ID
    firstName: string;
    lastName?: string;
    email?: string;
}

interface RosterCacheContextType {
    rosterCache: Record<string, GCRosterStudent[]>;
    getOrFetchGCRoster: (googleCourseId: string) => Promise<GCRosterStudent[]>;
}

const RosterCacheContext = createContext<RosterCacheContextType | undefined>(undefined);

export const RosterCacheProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Ephemeral in-memory RAM cache for Google Classroom rosters
    const [rosterCache, setRosterCache] = useState<Record<string, GCRosterStudent[]>>({});

    const rosterCacheRef = useRef(rosterCache);
    useEffect(() => {
        rosterCacheRef.current = rosterCache;
    }, [rosterCache]);

    const activeRequestsRef = useRef<Record<string, Promise<GCRosterStudent[]> | undefined>>({});

    const getOrFetchGCRoster = useCallback(async (googleCourseId: string): Promise<GCRosterStudent[]> => {
        if (!googleCourseId) return [];

        // 1. Check if roster is already cached in RAM
        if (rosterCacheRef.current[googleCourseId]) {
            return rosterCacheRef.current[googleCourseId];
        }

        // 2. Check if there is already an in-flight request for this course roster
        if (activeRequestsRef.current[googleCourseId]) {
            return activeRequestsRef.current[googleCourseId];
        }

        // 3. Otherwise, create a new promise and register it
        const fetchPromise = (async () => {
            try {// `[GoogleClassroom] 🏫 Auto-fetching roster for course ${googleCourseId}...`);
                const googleFetchRoster = httpsCallable<{ courseId: string }, { students: GCRosterStudent[] }>(functions, 'googleFetchRoster');
                const result = await googleFetchRoster({ courseId: googleCourseId });

                const students = result.data.students || [];
                setRosterCache(prev => ({ ...prev, [googleCourseId]: students }));
                return students;
            } catch (err) {
                console.error(`[GoogleClassroom] Failed to fetch roster for course ${googleCourseId}:`, err);
            } finally {
                // Remove from active requests when finished
                delete activeRequestsRef.current[googleCourseId];
            }
            return [];
        })();

        activeRequestsRef.current[googleCourseId] = fetchPromise;
        return fetchPromise;
    }, []);

    return (
        <RosterCacheContext.Provider value={{ rosterCache, getOrFetchGCRoster }}>
            {children}
        </RosterCacheContext.Provider>
    );
};

export const useRosterCache = () => {
    const context = useContext(RosterCacheContext);
    if (!context) {
        throw new Error('useRosterCache must be used within a RosterCacheProvider');
    }
    return context;
};
